import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { fetchMatchWithData, fetchTeamByGuid } from "../utils/dbutils";
import {
  kForehand,
  kKeypoint,
  kRally,
  kRallyBounceOverhead,
  kRallyDriveVolley,
  kRallyDropVolley,
  kRallyHalfVolley,
  kRallyOverhead,
  kRallyServeVolley,
  kRallySmash,
  kRallyVolley,
  kRallyVolleyPoach,
  kReturnIn,
  kReturning,
  kReturnInOffNet,
  kReturnPassingShot,
  kReturnVolleyed,
  kReturnWinner,
  kServeFirst,
  kServeSecond,
  kServing,
} from "../utils/pstsDefines";
import { drawArrowHead, interpolate } from "../utils/utils";

// ── Doubles per-player analysis helpers ────────────────────────────────────

const SHOT_TYPE_LABEL = {
  0: "Drive", 1: "Slice", 2: "Topspin", 3: "Volley", 4: "Overhead",
  5: "Lob", 6: "Dropshot", 7: "Other", 8: "DriveVolley", 9: "ServeVolley",
  10: "Smash", 11: "Approach", 12: "DropVolley", 13: "BounceOverhead",
  14: "VolleyPoach", 15: "HalfVolley", 16: "PassingShot", 17: "PowerShot",
  18: "Flat", 19: "Kick",
};

// All shot types that are typically played at or near the net.
const VOLLEY_SHOT_TYPES = new Set([
  kRallyVolley, kRallyOverhead, kRallyDriveVolley, kRallyServeVolley,
  kRallySmash, kRallyDropVolley, kRallyBounceOverhead, kRallyHalfVolley,
  kRallyVolleyPoach,
]);

// Normalize the x-coordinate of a ball so that values >= 50 always represent
// the same physical half (deuce court, server's right when at the bottom).
// Players at the bottom (y > 100) keep their x; players at the top mirror it.
function normalizeEventX(ball) {
  if (!ball) return null;
  if (ball.y > 100) return ball.x;
  if (ball.y < 100) return 100 - ball.x;
  return null; // exactly at net — ambiguous, skip
}

function buildDoublesPlayerStats(matches, playerGuid) {
  const playerData = {}; // guid -> { name, events[] }

  for (const m of matches) {
    if (!m.isDoubles) continue;
    const tAGuid = m.teamA?.guid ||
      (m.teamA?.player1?.guid && m.teamA?.player2?.guid
        ? `${m.teamA.player1.guid}_${m.teamA.player2.guid}` : null);
    const tBGuid = m.teamB?.guid ||
      (m.teamB?.player1?.guid && m.teamB?.player2?.guid
        ? `${m.teamB.player1.guid}_${m.teamB.player2.guid}` : null);

    let focusTeam = null;
    if (playerGuid === tAGuid || playerGuid === m.teamA?.player1?.guid || playerGuid === m.teamA?.player2?.guid)
      focusTeam = m.teamA;
    else if (playerGuid === tBGuid || playerGuid === m.teamB?.player1?.guid || playerGuid === m.teamB?.player2?.guid)
      focusTeam = m.teamB;
    if (!focusTeam) continue;

    const members = [focusTeam.player1, focusTeam.player2].filter((p) => p?.guid);
    for (const p of members) {
      if (!playerData[p.guid]) {
        playerData[p.guid] = {
          name: `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.guid.slice(0, 8),
          events: [],
        };
      }
    }
    const memberGuids = new Set(members.map((p) => p.guid));
    for (const set of m.sets ?? []) {
      for (const e of set.events ?? []) {
        if (memberGuids.has(e.playerGuid)) playerData[e.playerGuid].events.push(e);
      }
    }
  }

  const profiles = Object.values(playerData).map(({ name, events }) => {
    if (!events.length) return { name, hasData: false };
    const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
    const total = events.length;

    // Court side — normalized so deuce/right is consistent across ends
    let rightCount = 0, leftCount = 0;
    for (const e of events) {
      const b = parseBall(e.startBall);
      if (!b) continue;
      const nx = normalizeEventX(b);
      if (nx === null) continue;
      if (nx >= 50) rightCount++; else leftCount++;
    }
    const rightCourtPct = pct(rightCount, rightCount + leftCount);
    const primaryCourtSide =
      rightCourtPct >= 55 ? "right/deuce court" :
      rightCourtPct <= 45 ? "left/ad court" : "no strong preference (balanced)";

    // Net presence (atNet flag + y-depth of contact)
    const atNetCount = events.filter((e) => e.atNet === true).length;
    // Additional depth check: shots hit in y [80,120] (around the service line / close to net)
    const nearNetCount = events.filter((e) => {
      const b = parseBall(e.startBall);
      return b && b.y >= 80 && b.y <= 120;
    }).length;

    // Shot types
    const rallyEvents = events.filter((e) => e.skill === kRally || e.skill === kKeypoint);
    const shotTypeCounts = {};
    for (const e of rallyEvents) {
      const t = e.subskill2 ?? 0;
      shotTypeCounts[t] = (shotTypeCounts[t] ?? 0) + 1;
    }
    const netShotCount = rallyEvents.filter((e) => VOLLEY_SHOT_TYPES.has(e.subskill2 ?? -1)).length;
    const poachCount = events.filter((e) => (e.subskill2 ?? -1) === kRallyVolleyPoach).length;

    const topShotTypes = Object.entries(shotTypeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([t, count]) => ({
        type: SHOT_TYPE_LABEL[Number(t)] ?? `Type${t}`,
        count,
        pct: pct(count, rallyEvents.length),
      }));

    // Hand
    const fhCount = events.filter((e) => Number(e.hand) === 0).length;
    const bhCount = events.filter((e) => Number(e.hand) === 1).length;

    // Outcomes
    const winners = rallyEvents.filter((e) => e.outcome === 2 || e.result === 3).length;
    const errors = rallyEvents.filter((e) => e.outcome === 3 || e.outcome === 8 || e.result === 0).length;

    return {
      name,
      hasData: true,
      totalEvents: total,
      primaryCourtSide,
      rightCourtPct,
      leftCourtPct: 100 - rightCourtPct,
      atNetPct: pct(atNetCount, total),
      nearNetPct: pct(nearNetCount, total),
      rallyEventCount: rallyEvents.length,
      netShotPct: pct(netShotCount, rallyEvents.length),
      poachCount,
      topShotTypes,
      forehandPct: pct(fhCount, fhCount + bhCount),
      backhandPct: pct(bhCount, fhCount + bhCount),
      winnerPct: pct(winners, rallyEvents.length),
      errorPct: pct(errors, rallyEvents.length),
      serveCount: events.filter((e) => e.skill === kServing).length,
      returnCount: events.filter((e) => e.skill === kReturning).length,
    };
  });

  return profiles.some((p) => p.hasData) ? profiles : null;
}

const POINT_COLORS = {
  winner: "#22c55e",
  serverWon: "#7dd3fc",
  out: "#ef4444",
  receiverWon: "#fb923c",
};

const COURT_BASE = {
  width: 216,
  height: 468,
  tram: 27,
  serviceLine: 108,
};

function parseBall(ball) {
  if (!ball || typeof ball !== "string") return null;
  const [x, y] = ball.split(",").map((n) => Number(n));
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function normalisePointForServer(pt, flip) {
  if (!pt) return null;
  if (!flip) return pt;
  return { x: 100 - pt.x, y: 200 - pt.y };
}

function pointNumberFromEvent(e) {
  const pgs = Number(e?.playerGameScore ?? 0);
  const ogs = Number(e?.oppositionGameScore ?? 0);
  return pgs + ogs + 1;
}

function deuceAdFromServe(serveEvent) {
  return pointNumberFromEvent(serveEvent) % 2 === 1 ? "deuce" : "ad";
}

// 6 return zones spanning the singles court (between the tram lines): x in [12.5, 87.5].
// Each zone is (87.5 - 12.5) / 6 = 12.5% wide.
function zone6(x) {
  if (!Number.isFinite(x)) return -1;
  if (x < 12.5 || x > 87.5) return -1;
  return Math.min(5, Math.max(0, Math.floor((x - 12.5) / 12.5)));
}

// Tram (doubles alley) is 12.5% of total court width on each side.
// Singles court spans x in [12.5, 87.5]. Each service box is 37.5 wide.
// Each serve zone (Wide / Body / T) is 12.5 wide.
// Zones are labelled from server's perspective (Wide = far from centre line, T = next to centre line).
//   Deuce side (ball lands x in [12.5, 50]):  Wide=[12.5,25)  Body=[25,37.5)  T=[37.5,50]
//   Ad side    (ball lands x in [50, 87.5]):  T=[50,62.5)     Body=[62.5,75)  Wide=[75,87.5]
function serveZone3(x, side) {
  if (!Number.isFinite(x)) return -1;
  if (side === "deuce") {
    if (x < 12.5 || x > 50) return -1;
    if (x < 25) return 0;
    if (x < 37.5) return 1;
    return 2;
  }
  if (side === "ad") {
    if (x < 50 || x > 87.5) return -1;
    if (x < 62.5) return 2;
    if (x < 75) return 1;
    return 0;
  }
  return -1;
}

function getPointWinnerId(lastEvent, match) {
  const result = Number(lastEvent?.result ?? 1);
  const hitterId = lastEvent?.playerGuid;
  if (!hitterId) return null;
  if (result === 3) return hitterId;
  if (result === 0) {
    const p1 = match?.player1?.guid ?? match?.player1Guid;
    const p2 = match?.player2?.guid ?? match?.player2Guid;
    if (hitterId === p1) return p2;
    if (hitterId === p2) return p1;
  }
  return null;
}

function shotClass(shot, pointWinnerId, serverId) {
  const result = Number(shot?.result ?? 1);
  if (result === 3 && pointWinnerId && shot.playerGuid === pointWinnerId) {
    return "winner";
  }
  if (result === 0) return "out";
  if (pointWinnerId && pointWinnerId === serverId) return "serverWon";
  return "receiverWon";
}

function buildServePoints(matches, playerGuid, serveType, perspective = "serving", specificPlayerGuid = null) {
  const sideArrays = { deuce: [], ad: [], all: [] };

  for (const match of matches) {
    // Doubles-aware focused-entity detection.
    const isDoubles = !!match.isDoubles;
    let focusedGuid = null;
    let focusIsTeamA = false; // only meaningful for doubles
    if (isDoubles) {
      const tAGuid = match.teamA?.guid ||
        (match.teamA?.player1?.guid && match.teamA?.player2?.guid
          ? `${match.teamA.player1.guid}_${match.teamA.player2.guid}` : null);
      const tBGuid = match.teamB?.guid ||
        (match.teamB?.player1?.guid && match.teamB?.player2?.guid
          ? `${match.teamB.player1.guid}_${match.teamB.player2.guid}` : null);
      if (playerGuid === tAGuid ||
          playerGuid === match.teamA?.player1?.guid ||
          playerGuid === match.teamA?.player2?.guid) {
        focusIsTeamA = true;
        focusedGuid = tAGuid;
      } else if (playerGuid === tBGuid ||
          playerGuid === match.teamB?.player1?.guid ||
          playerGuid === match.teamB?.player2?.guid) {
        focusIsTeamA = false;
        focusedGuid = tBGuid;
      }
    } else {
      // Resolve which player in this match is the focused one. The selected
      // player's guid may match either the nested player1/player2 guid or the
      // top-level player1Guid/player2Guid. Use the nested form when available
      // because that's what events store in event.playerGuid.
      const p1Guid = match?.player1?.guid ?? match?.player1Guid ?? null;
      const p2Guid = match?.player2?.guid ?? match?.player2Guid ?? null;
      if (p1Guid === playerGuid || match?.player1Guid === playerGuid) {
        focusedGuid = p1Guid;
      } else if (p2Guid === playerGuid || match?.player2Guid === playerGuid) {
        focusedGuid = p2Guid;
      }
    }
    if (!focusedGuid) continue; // focused player/team isn't in this match

    for (const set of match.sets ?? []) {
      const events = set.events ?? [];
      let pointEvents = [];
      let currentServe = null;

      for (const event of events) {
        if (event.skill === kServing) {
          pointEvents = [event];
          currentServe = event;
        } else if (currentServe) {
          pointEvents.push(event);
        } else {
          continue;
        }

        const result = Number(event?.result ?? 1);
        if (result !== 0 && result !== 3) continue;

        const serve = currentServe;
        currentServe = null;

        if (!serve) continue;
        if (isDoubles) {
          // Use isPlayer1 (true = teamA) to identify the server's team.
          // Fall back to guid comparison for older data that may lack isPlayer1.
          const serverIsTeamA =
            serve.isPlayer1 === true ||
            serve.playerGuid === match.teamA?.player1?.guid ||
            serve.playerGuid === match.teamA?.player2?.guid;
          if (perspective === "serving") {
            if (focusIsTeamA !== serverIsTeamA) continue;
          } else {
            // returning: focused team must be the receiver's team
            if (focusIsTeamA === serverIsTeamA) continue;
          }
        } else {
          if (perspective === "serving") {
            if (serve.playerGuid !== focusedGuid) continue;
          } else {
            // "returning": focused player is the receiver; server is the opponent.
            if (!serve.playerGuid || serve.playerGuid === focusedGuid) continue;
          }
        }
        // Narrow to a specific individual within a doubles team.
        if (isDoubles && specificPlayerGuid && perspective === "serving") {
          if (serve.playerGuid !== specificPlayerGuid) continue;
        }
        if (serve.subskill1 !== serveType) continue;

        const side = deuceAdFromServe(serve);
        // For doubles, determine point winner by team membership (isPlayer1 / guid fallback).
        let pointWinnerId;
        if (isDoubles) {
          const lastResult = Number(event?.result ?? 1);
          const serverIsTeamA =
            serve.isPlayer1 === true ||
            serve.playerGuid === match.teamA?.player1?.guid ||
            serve.playerGuid === match.teamA?.player2?.guid;
          if (lastResult === 3) {
            // Individual winner — compatible with shotClass's playerGuid === pointWinnerId check.
            pointWinnerId = event.playerGuid;
          } else if (lastResult === 0) {
            const lastHitterIsTeamA =
              event.isPlayer1 === true ||
              event.playerGuid === match.teamA?.player1?.guid ||
              event.playerGuid === match.teamA?.player2?.guid;
            // Error by lastHitter → their team loses. Server wins if error was by receiver's team.
            const serverTeamWon = lastHitterIsTeamA !== serverIsTeamA;
            // Return serve.playerGuid so pointWinnerId === serverId makes shotClass say "serverWon".
            pointWinnerId = serverTeamWon ? serve.playerGuid : null;
          } else {
            pointWinnerId = null;
          }
        } else {
          pointWinnerId = getPointWinnerId(event, match);
        }
        const flip = (parseBall(serve.startBall)?.y ?? 200) < 100;

        const serveStart = normalisePointForServer(parseBall(serve.startBall), flip);
        const serveEnd = normalisePointForServer(parseBall(serve.endBall), flip);
        const serveZone = serveZone3(serveEnd?.x ?? NaN, side);

        const returnEvent = pointEvents.find((e) => e.skill === kReturning) ?? null;
        // Narrow to a specific returner within a doubles team.
        if (isDoubles && specificPlayerGuid && perspective === "returning") {
          if (!returnEvent || returnEvent.playerGuid !== specificPlayerGuid) continue;
        }
        const servePlus1Event = returnEvent
          ? pointEvents.find(
              (e) =>
                (e.skill === kRally || e.skill === kKeypoint) &&
                e.playerGuid === serve.playerGuid &&
                e.timestamp >= returnEvent.timestamp
            ) ?? null
          : null;
        const returnPlus1Event = servePlus1Event
          ? pointEvents.find(
              (e) =>
                (e.skill === kRally || e.skill === kKeypoint) &&
                e.playerGuid !== serve.playerGuid &&
                e.timestamp >= servePlus1Event.timestamp
            ) ?? null
          : null;

        const serveShot = serveStart && serveEnd
          ? { start: serveStart, end: serveEnd, cls: shotClass(serve, pointWinnerId, serve.playerGuid) }
          : null;

        let returnShot = null;
        let returnZone = -1;
        if (returnEvent) {
          const s = normalisePointForServer(parseBall(returnEvent.startBall), flip);
          const e2 = normalisePointForServer(parseBall(returnEvent.endBall), flip);
          returnZone = zone6(e2?.x ?? NaN);
          if (s && e2) returnShot = { start: s, end: e2, cls: shotClass(returnEvent, pointWinnerId, serve.playerGuid) };
        }

        let servePlus1Shot = null;
        let servePlus1Zone = -1;
        if (servePlus1Event) {
          const s = normalisePointForServer(parseBall(servePlus1Event.startBall), flip);
          const e2 = normalisePointForServer(parseBall(servePlus1Event.endBall), flip);
          servePlus1Zone = zone6(e2?.x ?? NaN);
          if (s && e2) servePlus1Shot = { start: s, end: e2, cls: shotClass(servePlus1Event, pointWinnerId, serve.playerGuid) };
        }

        let returnPlus1Shot = null;
        let returnPlus1Zone = -1;
        if (returnPlus1Event) {
          const s = normalisePointForServer(parseBall(returnPlus1Event.startBall), flip);
          const e2 = normalisePointForServer(parseBall(returnPlus1Event.endBall), flip);
          returnPlus1Zone = zone6(e2?.x ?? NaN);
          if (s && e2) returnPlus1Shot = { start: s, end: e2, cls: shotClass(returnPlus1Event, pointWinnerId, serve.playerGuid) };
        }

        // Breakpoint detection: from the serve event's player game scores.
        // serve.playerGuid is the server, so playerGameScore = server's game
        // score, oppositionGameScore = receiver's. Receiver has BP (server
        // faces BP) when receiver is ahead with a score >= 3 (and server
        // hasn't reached advantage).
        const sgs = Number(serve?.playerGameScore ?? 0);
        const rgs = Number(serve?.oppositionGameScore ?? 0);
        const isBreakpoint = rgs >= 3 && rgs > sgs && sgs < 4;

        // Hand for each shot (kForehand=0, kBackhand=1, kUnknown=2).
        const servePlus1Hand = servePlus1Event ? Number(servePlus1Event.hand ?? 2) : -1;
        const returnHand = returnEvent ? Number(returnEvent.hand ?? 2) : -1;
        const returnPlus1Hand = returnPlus1Event ? Number(returnPlus1Event.hand ?? 2) : -1;

        // Serve+1 contact point: y in normalised coords. Server side is the
        // bottom of the court (y in [100,200]); baseline is at y=200.
        // "On or inside the baseline" = y <= 200.
        // Serve+1 contact point: y in normalised coords. Server side is the
        // bottom of the court (y in [100,200]); baseline is at y=200.
        // "Inside the court" for the server = y < 200 (past baseline toward net).
        // "Behind the baseline" = y > 200.
        const sp1Start = servePlus1Shot?.start ?? null;
        const servePlus1InsideBaseline = sp1Start ? sp1Start.y <= 200 : null;

        // Returner is on the OPPOSITE side: y in [0,100], baseline at y=0.
        // "Inside the court" for the receiver = y > 0 (past baseline toward net).
        // "Behind the baseline" = y < 0.
        const retStart = returnShot?.start ?? null;
        const returnInsideBaseline = retStart ? retStart.y >= 0 : null;
        const ret1Start = returnPlus1Shot?.start ?? null;
        const returnPlus1InsideBaseline = ret1Start ? ret1Start.y >= 0 : null;

        // Serve in play: there's a return event, OR the serve was an outright
        // winner (result === 3 on the serve event itself).
        const serveIn = !!returnEvent || Number(serve?.result ?? 0) === 3;
        // Return into play: the return event landed in (kReturnIn,
        // kReturnInOffNet, kReturnVolleyed, kReturnWinner, kReturnPassingShot).
        let returnIn = false;
        if (returnEvent) {
          const ro = Number(returnEvent.outcome ?? -1);
          returnIn = (
            ro === kReturnIn ||
            ro === kReturnInOffNet ||
            ro === kReturnVolleyed ||
            ro === kReturnWinner ||
            ro === kReturnPassingShot
          );
        }

        const pointWonByServer = pointWinnerId && pointWinnerId === serve.playerGuid;
        const pointWonByFocused =
          perspective === "serving" ? pointWonByServer : !!pointWinnerId && !pointWonByServer;

        const rec = {
          side,
          serveZone,
          returnZone,
          servePlus1Zone,
          returnPlus1Zone,
          serveShot,
          returnShot,
          servePlus1Shot,
          returnPlus1Shot,
          servePlus1Hand,
          returnHand,
          returnPlus1Hand,
          servePlus1InsideBaseline,
          returnInsideBaseline,
          returnPlus1InsideBaseline,
          isBreakpoint,
          serveIn,
          returnIn,
          pointWonByServer,
          pointWonByFocused,
        };

        sideArrays.all.push(rec);
        sideArrays[side].push(rec);
      }
    }
  }

  return sideArrays;
}

const STAGE_ZONES = {
  serve: 3,
  return: 6,
  servePlus1: 6,
  returnPlus1: 6,
};

function passesFilter(filterSet, value) {
  if (!filterSet || filterSet.size === 0) return true;
  return filterSet.has(value);
}

function filterPoints(points, filters, stages) {
  return points.filter((p) =>
    stages.every((s) => passesFilter(filters[s], p[`${s}Zone`]))
  );
}

function buildMatrix(points, rowKey, colKey, rows, cols) {
  const m = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (const p of points) {
    const r = p[rowKey];
    const c = p[colKey];
    if (r >= 0 && r < rows && c >= 0 && c < cols) m[r][c] += 1;
  }
  return m;
}

function countByZone(points, key, n) {
  const arr = Array(n).fill(0);
  for (const p of points) {
    const v = p[key];
    if (v >= 0 && v < n) arr[v] += 1;
  }
  return arr;
}

function toggleSetValue(setObj, value) {
  const next = new Set(setObj);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

function isRowColAllowed(rowIndex, colIndex, selectedRows, selectedCols) {
  const rowAllowed = selectedRows.size === 0 || selectedRows.has(rowIndex);
  const colAllowed = selectedCols.size === 0 || selectedCols.has(colIndex);
  return rowAllowed && colAllowed;
}

function MatrixTable({
  rows,
  rowLabels,
  colLabels,
  selectedRows,
  selectedCols,
  onToggleRow,
  onToggleCol,
  onClear,
}) {
  return (
    <div className="overflow-x-auto">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-base-content/70">Click row/column headers to filter</span>
        <button className="btn btn-ghost btn-xs" onClick={onClear}>Clear</button>
      </div>
      <table className="table table-xs">
        <thead>
          <tr>
            <th />
            {colLabels.map((label, colIndex) => {
              const active = selectedCols.size === 0 || selectedCols.has(colIndex);
              return (
                <th key={label} className="text-center">
                  <button
                    className={`btn btn-ghost btn-xs ${active ? "btn-active" : "opacity-50"}`}
                    onClick={() => onToggleCol(colIndex)}
                  >
                    {label}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const rowActive = selectedRows.size === 0 || selectedRows.has(rowIndex);
            return (
              <tr key={rowLabels[rowIndex]}>
                <th className="whitespace-nowrap">
                  <button
                    className={`btn btn-ghost btn-xs ${rowActive ? "btn-active" : "opacity-50"}`}
                    onClick={() => onToggleRow(rowIndex)}
                  >
                    {rowLabels[rowIndex]}
                  </button>
                </th>
                {row.map((value, colIndex) => {
                  const allowed = isRowColAllowed(
                    rowIndex,
                    colIndex,
                    selectedRows,
                    selectedCols
                  );
                  return (
                    <td
                      key={`${rowIndex}-${colIndex}`}
                      className={`text-center ${allowed ? "" : "opacity-30"}`}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function drawSimpleCourt(ctx, width, height, options = {}) {
  const { showServeZones = false, showReturnZones = false } = options;

  ctx.fillStyle = "#0a4fa3";
  ctx.fillRect(0, 0, width, height);

  const outerPadX = 40;
  const outerPadY = 60;
  const availW = Math.max(1, width - outerPadX * 2);
  const availH = Math.max(1, height - outerPadY * 2);
  // Shrink court to 90% of available area to leave more visible top/bottom margin.
  const scale = Math.min(availW / COURT_BASE.width, availH / COURT_BASE.height) * 0.9;

  const courtW = COURT_BASE.width * scale;
  const courtH = COURT_BASE.height * scale;
  const xmargin = (width - courtW) / 2;
  const ymargin = (height - courtH) / 2;

  const tram = COURT_BASE.tram * scale;
  const service = COURT_BASE.serviceLine * scale;
  const netY = ymargin + courtH / 2;

  ctx.lineWidth = Math.max(2, 2.5 * scale);
  ctx.strokeStyle = "#ffffff";

  ctx.strokeRect(xmargin, ymargin, courtW, courtH);
  ctx.beginPath();
  ctx.moveTo(xmargin, netY);
  ctx.lineTo(xmargin + courtW, netY);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(xmargin + tram, ymargin);
  ctx.lineTo(xmargin + tram, ymargin + courtH);
  ctx.moveTo(xmargin + courtW - tram, ymargin);
  ctx.lineTo(xmargin + courtW - tram, ymargin + courtH);
  ctx.moveTo(xmargin + tram, ymargin + service);
  ctx.lineTo(xmargin + courtW - tram, ymargin + service);
  ctx.moveTo(xmargin + tram, ymargin + courtH - service);
  ctx.lineTo(xmargin + courtW - tram, ymargin + courtH - service);
  ctx.moveTo(xmargin + courtW / 2, ymargin + service);
  ctx.lineTo(xmargin + courtW / 2, ymargin + courtH - service);
  ctx.stroke();

  if (showServeZones) {
    // Draw the 1/3 + 1/3 + 1/3 dividers inside each service box on the
    // receiver's (top) half. Service box spans from singles sideline to
    // centre service line, and from baseline-side service line to the net.
    // Each zone is 1/6 of the singles court width.
    const zone = (courtW - 2 * tram) / 6; // 12.5% of total court width
    const yTop = ymargin + service;        // service line
    const yBottom = ymargin + courtH / 2;  // net

    ctx.save();
    ctx.strokeStyle = "#d1d5db"; // light gray
    ctx.lineWidth = Math.max(1.5, 1.5 * scale);
    ctx.setLineDash([Math.max(4, 6 * scale), Math.max(4, 6 * scale)]);
    ctx.beginPath();
    // Deuce-side service box dividers (server's perspective: x = 25, 37.5)
    ctx.moveTo(xmargin + tram + zone, yTop);
    ctx.lineTo(xmargin + tram + zone, yBottom);
    ctx.moveTo(xmargin + tram + 2 * zone, yTop);
    ctx.lineTo(xmargin + tram + 2 * zone, yBottom);
    // Ad-side service box dividers (x = 62.5, 75)
    ctx.moveTo(xmargin + courtW / 2 + zone, yTop);
    ctx.lineTo(xmargin + courtW / 2 + zone, yBottom);
    ctx.moveTo(xmargin + courtW / 2 + 2 * zone, yTop);
    ctx.lineTo(xmargin + courtW / 2 + 2 * zone, yBottom);
    ctx.stroke();
    ctx.restore();

    // Zone labels (centred between service line and net)
    ctx.save();
    ctx.fillStyle = "#d1d5db";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `bold ${Math.max(10, 11 * scale)}px sans-serif`;
    const labelY = (yTop + yBottom) / 2;
    const deuceLabels = ["W", "B", "T"];
    const adLabels = ["T", "B", "W"];
    for (let i = 0; i < 3; i++) {
      const dx = xmargin + tram + zone * (i + 0.5);
      ctx.fillText(deuceLabels[i], dx, labelY);
      const ax = xmargin + courtW / 2 + zone * (i + 0.5);
      ctx.fillText(adLabels[i], ax, labelY);
    }
    ctx.restore();
  }

  if (showReturnZones) {
    // 6 return zones span the singles court (between the tram lines).
    const zw = (courtW - 2 * tram) / 6;
    const xStart = xmargin + tram;
    const yTop = ymargin + courtH / 2; // net
    const yBottom = ymargin + courtH;

    ctx.save();
    ctx.strokeStyle = "#d1d5db"; // light gray
    ctx.lineWidth = Math.max(1.5, 1.5 * scale);
    ctx.setLineDash([Math.max(4, 6 * scale), Math.max(4, 6 * scale)]);
    ctx.beginPath();
    for (let i = 1; i < 6; i++) {
      const x = xStart + zw * i;
      ctx.moveTo(x, yTop);
      ctx.lineTo(x, yBottom);
    }
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.fillStyle = "#d1d5db";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `bold ${Math.max(10, 11 * scale)}px sans-serif`;
    const labelY = ymargin + courtH - Math.max(8, 10 * scale);
    for (let i = 0; i < 6; i++) {
      ctx.fillText(`Z${i + 1}`, xStart + zw * (i + 0.5), labelY);
    }
    ctx.restore();
  }

  return {
    x: xmargin,
    y: ymargin,
    width: courtW,
    height: courtH,
    scale,
  };
}

function ShotsCourt({ shots, showSource = false, title, showServeZones = false, showReturnZones = false, hideErrors = false, showPaths = false }) {
  const canvasRef = useRef(null);

  // Fixed compact size: court drawn ~240x520, with 40px side and 60px top/bottom padding.
  const CANVAS_W = 320;
  const CANVAS_H = 640;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = CANVAS_W * 2;
    canvas.height = CANVAS_H * 2;
    canvas.style.width = `${CANVAS_W}px`;
    canvas.style.height = `${CANVAS_H}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const court = drawSimpleCourt(ctx, canvas.width, canvas.height, { showServeZones, showReturnZones });
    // Dots and arrows are 30% smaller than before.
    const dotR = Math.max(3.5, 6 * court.scale * 0.7);
    const arrowLen = Math.max(7, 14 * court.scale * 0.7);

    const visibleShots = hideErrors ? shots.filter((s) => s.cls !== "out") : shots;

    if (showPaths) {
      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.lineWidth = Math.max(0.5, 0.8 * court.scale);
      for (const shot of visibleShots) {
        const sx = court.x + (shot.start.x / 100) * court.width;
        const sy = court.y + (shot.start.y / 200) * court.height;
        const ex = court.x + (shot.end.x / 100) * court.width;
        const ey = court.y + (shot.end.y / 200) * court.height;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, ey);
        ctx.stroke();
      }
      ctx.restore();
    }

    for (const shot of visibleShots) {
      const sx = court.x + (shot.start.x / 100) * court.width;
      const sy = court.y + (shot.start.y / 200) * court.height;
      const ex = court.x + (shot.end.x / 100) * court.width;
      const ey = court.y + (shot.end.y / 200) * court.height;

      if (showSource) {
        const pp = interpolate({ x: sx, y: sy }, { x: ex, y: ey }, 0.03);
        drawArrowHead(
          ctx,
          { x: sx, y: sy },
          pp,
          arrowLen,
          0.45,
          POINT_COLORS[shot.cls],
          POINT_COLORS[shot.cls]
        );
      }

      ctx.beginPath();
      ctx.arc(ex, ey, dotR, 0, Math.PI * 2);
      ctx.fillStyle = POINT_COLORS[shot.cls];
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#1f2937";
      ctx.stroke();
    }
  }, [shots, showSource, showServeZones, showReturnZones, hideErrors, showPaths]);

  return (
    <div className="border border-base-300 rounded-box p-2 bg-base-200 inline-block" style={{ width: CANVAS_W + 16 }}>
      <h4 className="text-sm font-semibold mb-2">{title}</h4>
      <canvas ref={canvasRef} />
      <div className="text-xs mt-2 flex gap-2 flex-wrap">
        <span><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" />Winner</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-sky-300 mr-1" />Server won</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1" />Out</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-1" />Receiver won</span>
      </div>
    </div>
  );
}

function StagePanel({ heading, children, court }) {
  return (
    <div className="flex flex-row flex-wrap gap-3 items-start">
      {court}
      <div className="border border-base-300 rounded-box p-2 flex-1 min-w-[260px]">
        <h4 className="text-sm font-semibold mb-2">{heading}</h4>
        {children}
      </div>
    </div>
  );
}

function ServeSection({ title, points, sideFilter, hideErrors, showPaths }) {
  const current = points[sideFilter] ?? points.all;

  // Cascading filters. Each set of selected zone indices acts as a filter
  // for that stage, and downstream stages inherit upstream filters.
  const [serveSel, setServeSel] = useState(new Set());
  const [returnSel, setReturnSel] = useState(new Set());
  const [servePlus1Sel, setServePlus1Sel] = useState(new Set());
  const [returnPlus1Sel, setReturnPlus1Sel] = useState(new Set());

  useEffect(() => {
    setServeSel(new Set());
    setReturnSel(new Set());
    setServePlus1Sel(new Set());
    setReturnPlus1Sel(new Set());
  }, [sideFilter]);

  const filters = useMemo(
    () => ({ serve: serveSel, return: returnSel, servePlus1: servePlus1Sel, returnPlus1: returnPlus1Sel }),
    [serveSel, returnSel, servePlus1Sel, returnPlus1Sel]
  );

  // Stage point sets after cascading upstream filters.
  const returnStagePoints = useMemo(
    () => filterPoints(current, filters, ["serve"]),
    [current, filters]
  );
  const servePlus1StagePoints = useMemo(
    () => filterPoints(current, filters, ["serve", "return"]),
    [current, filters]
  );
  const returnPlus1StagePoints = useMemo(
    () => filterPoints(current, filters, ["serve", "return", "servePlus1"]),
    [current, filters]
  );

  // Serve zone counts: shown next to W/B/T toggle buttons. Use unfiltered
  // counts so the user always sees the full distribution.
  const serveZoneCounts = useMemo(
    () => countByZone(current, "serveZone", STAGE_ZONES.serve),
    [current]
  );

  // Matrices are derived from the relevant stage's point set.
  const returnMatrix = useMemo(
    () => buildMatrix(returnStagePoints, "serveZone", "returnZone", STAGE_ZONES.serve, STAGE_ZONES.return),
    [returnStagePoints]
  );
  const servePlus1Matrix = useMemo(
    () => buildMatrix(servePlus1StagePoints, "returnZone", "servePlus1Zone", STAGE_ZONES.return, STAGE_ZONES.servePlus1),
    [servePlus1StagePoints]
  );
  const returnPlus1Matrix = useMemo(
    () => buildMatrix(returnPlus1StagePoints, "servePlus1Zone", "returnPlus1Zone", STAGE_ZONES.servePlus1, STAGE_ZONES.returnPlus1),
    [returnPlus1StagePoints]
  );

  // Shots displayed at each stage: cascading filters applied (including current stage's own selection).
  const serveShots = useMemo(
    () => filterPoints(current, filters, ["serve"]).map((p) => p.serveShot).filter(Boolean),
    [current, filters]
  );
  const returnShots = useMemo(
    () => filterPoints(current, filters, ["serve", "return"]).map((p) => p.returnShot).filter(Boolean),
    [current, filters]
  );
  const servePlus1Shots = useMemo(
    () => filterPoints(current, filters, ["serve", "return", "servePlus1"]).map((p) => p.servePlus1Shot).filter(Boolean),
    [current, filters]
  );
  const returnPlus1Shots = useMemo(
    () => filterPoints(current, filters, ["serve", "return", "servePlus1", "returnPlus1"]).map((p) => p.returnPlus1Shot).filter(Boolean),
    [current, filters]
  );

  const serveLabels = ["W", "B", "T"];

  return (
    <div className="border border-base-300 rounded-box p-4 flex flex-col gap-4">
      <h2 className="text-lg font-bold">{title}</h2>

      <StagePanel
        heading="1. Serves (click W / B / T to filter)"
        court={
          <ShotsCourt
            title="Serve locations"
            shots={serveShots}
            showSource={false}
            showServeZones={true}
            hideErrors={hideErrors}
            showPaths={showPaths}
          />
        }
      >
        <div className="flex items-center gap-2 mb-2">
          <button className="btn btn-ghost btn-xs" onClick={() => setServeSel(new Set())}>
            Clear
          </button>
        </div>
        <div className="flex gap-2">
          {serveLabels.map((label, i) => {
            const active = serveSel.size === 0 || serveSel.has(i);
            return (
              <button
                key={label}
                className={`btn btn-sm flex-1 ${active ? "btn-primary" : "btn-ghost opacity-60"}`}
                onClick={() => setServeSel((prev) => toggleSetValue(prev, i))}
              >
                <span className="font-bold mr-2">{label}</span>
                <span>{serveZoneCounts[i]}</span>
              </button>
            );
          })}
        </div>
      </StagePanel>

      <StagePanel
        heading="2. Return zones by serve zone (3 x 6)"
        court={
          <ShotsCourt
            title="Return shots"
            shots={returnShots}
            showSource={true}
            showReturnZones={true}
            hideErrors={hideErrors}
            showPaths={showPaths}
          />
        }
      >
        <MatrixTable
          rows={returnMatrix}
          rowLabels={serveLabels}
          colLabels={["Z1", "Z2", "Z3", "Z4", "Z5", "Z6"]}
          selectedRows={serveSel}
          selectedCols={returnSel}
          onToggleRow={(i) => setServeSel((prev) => toggleSetValue(prev, i))}
          onToggleCol={(j) => setReturnSel((prev) => toggleSetValue(prev, j))}
          onClear={() => {
            setServeSel(new Set());
            setReturnSel(new Set());
          }}
        />
      </StagePanel>

      <StagePanel
        heading="3. Serve+1 zones by return zone (6 x 6)"
        court={
          <ShotsCourt
            title="Serve+1 shots"
            shots={servePlus1Shots}
            showSource={true}
            showReturnZones={true}
            hideErrors={hideErrors}
            showPaths={showPaths}
          />
        }
      >
        <MatrixTable
          rows={servePlus1Matrix}
          rowLabels={["R1", "R2", "R3", "R4", "R5", "R6"]}
          colLabels={["S1-1", "S1-2", "S1-3", "S1-4", "S1-5", "S1-6"]}
          selectedRows={returnSel}
          selectedCols={servePlus1Sel}
          onToggleRow={(i) => setReturnSel((prev) => toggleSetValue(prev, i))}
          onToggleCol={(j) => setServePlus1Sel((prev) => toggleSetValue(prev, j))}
          onClear={() => {
            setReturnSel(new Set());
            setServePlus1Sel(new Set());
          }}
        />
      </StagePanel>

      <StagePanel
        heading="4. Return+1 zones by Serve+1 zone (6 x 6)"
        court={
          <ShotsCourt
            title="Return+1 shots"
            shots={returnPlus1Shots}
            showSource={true}
            showReturnZones={true}
            hideErrors={hideErrors}
            showPaths={showPaths}
          />
        }
      >
        <MatrixTable
          rows={returnPlus1Matrix}
          rowLabels={["S1-1", "S1-2", "S1-3", "S1-4", "S1-5", "S1-6"]}
          colLabels={["R1-1", "R1-2", "R1-3", "R1-4", "R1-5", "R1-6"]}
          selectedRows={servePlus1Sel}
          selectedCols={returnPlus1Sel}
          onToggleRow={(i) => setServePlus1Sel((prev) => toggleSetValue(prev, i))}
          onToggleCol={(j) => setReturnPlus1Sel((prev) => toggleSetValue(prev, j))}
          onClear={() => {
            setServePlus1Sel(new Set());
            setReturnPlus1Sel(new Set());
          }}
        />
      </StagePanel>
    </div>
  );
}

// ── AI Report helpers ───────────────────────────────────────────────────────
const OPENAI_KEY_LS = "psts_openai_key";
const OPENAI_MODEL_LS = "psts_openai_model";
const SAVED_QUESTIONS_LS = "psts_ai_followup_questions";
const MAX_SAVED_QUESTIONS = 50;

function loadSavedQuestions() {
  try {
    const raw = localStorage.getItem(SAVED_QUESTIONS_LS);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((q) => typeof q === "string") : [];
  } catch {
    return [];
  }
}

function persistSavedQuestions(list) {
  try {
    localStorage.setItem(SAVED_QUESTIONS_LS, JSON.stringify(list.slice(0, MAX_SAVED_QUESTIONS)));
  } catch {
    /* ignore quota errors */
  }
}

function renderInlineMd(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyPrefix}-${i}`}>{p.slice(2, -2)}</strong>
    ) : (
      p
    )
  );
}

function MarkdownReport({ text }) {
  if (!text) return null;
  const elements = [];
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    // Capture leading-space indent so nested bullets render with extra padding,
    // matching the look of the PDF export.
    const indentMatch = raw.match(/^(\s*)/);
    const indentSpaces = indentMatch ? indentMatch[1].length : 0;
    const indentLevel = Math.min(3, Math.floor(indentSpaces / 2));
    const indentClass = ["pl-0", "pl-6", "pl-10", "pl-14"][indentLevel];
    const line = raw.trimStart();

    if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={i}
          className="text-lg font-bold mt-6 mb-3 text-primary border-b-2 border-primary/30 pb-1.5"
        >
          {renderInlineMd(line.slice(3), i)}
        </h2>
      );
    } else if (line.startsWith("### ")) {
      elements.push(
        <h3 key={i} className="text-sm font-bold uppercase tracking-wide mt-4 mb-2 text-base-content/80">
          {renderInlineMd(line.slice(4), i)}
        </h3>
      );
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      elements.push(
        <div key={i} className={`flex gap-3 ${indentClass} my-1`}>
          <span className="shrink-0 text-primary font-bold mt-[1px] select-none">•</span>
          <p className="text-sm leading-relaxed flex-1">{renderInlineMd(line.slice(2), i)}</p>
        </div>
      );
    } else if (!line.trim()) {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(
        <p key={i} className={`text-sm leading-relaxed ${indentClass} my-1`}>
          {renderInlineMd(line, i)}
        </p>
      );
    }
  }
  return <div className="flex flex-col">{elements}</div>;
}

const SERVE_ZONE_LABELS = ["Wide", "Body", "T"];
const RETURN_ZONE_LABELS = ["Z1 (far AdCourt)", "Z2", "Z3", "Z4", "Z5", "Z6 (far DeuceCourt)"];

function summariseSidePoints(points) {
  // points: array of records produced by buildServePoints (filtered to one side).
  if (!points || points.length === 0) return null;
  const total = points.length;
  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

  const serveZoneStats = SERVE_ZONE_LABELS.map((label, i) => {
    const inZone = points.filter((p) => p.serveZone === i);
    const won = inZone.filter((p) => p.serveShot?.cls === "serverWon" || p.serveShot?.cls === "winner").length;
    const aces = inZone.filter((p) => p.serveShot?.cls === "winner").length;
    const faults = inZone.filter((p) => p.serveShot?.cls === "out").length;
    return {
      zone: label,
      attempts: inZone.length,
      pct: pct(inZone.length, total),
      serverWonPct: pct(won, inZone.length),
      aces,
      faults,
    };
  });

  const returnZoneStats = RETURN_ZONE_LABELS.map((label, i) => {
    const inZone = points.filter((p) => p.returnZone === i);
    const returnerWon = inZone.filter((p) => p.serveShot?.cls === "receiverWon").length;
    return {
      zone: label,
      attempts: inZone.length,
      pct: pct(inZone.length, total),
      returnerWonPct: pct(returnerWon, inZone.length),
    };
  });

  const servePlus1Stats = RETURN_ZONE_LABELS.map((label, i) => {
    const inZone = points.filter((p) => p.servePlus1Zone === i);
    const won = inZone.filter((p) => p.serveShot?.cls === "serverWon" || p.serveShot?.cls === "winner").length;
    return {
      zone: label,
      attempts: inZone.length,
      serverWonPct: pct(won, inZone.length),
    };
  });

  // ── Hand-based summaries ────────────────────────────────────────────────
  // Serve+1 by hand (server's first shot after the return).
  const sp1Pts = points.filter((p) => p.servePlus1Hand === kForehand || p.servePlus1Hand === 1);
  const servePlus1HandStat = (handVal) => {
    const arr = points.filter((p) => p.servePlus1Hand === handVal);
    const won = arr.filter((p) => p.pointWonByServer).length;
    const positioned = arr.filter((p) => p.servePlus1InsideBaseline !== null);
    const insideCourt = positioned.filter((p) => p.servePlus1InsideBaseline === true).length;
    const behindBaseline = positioned.filter((p) => p.servePlus1InsideBaseline === false).length;
    return {
      opportunities: arr.length,
      pctOfAllPoints: pct(arr.length, total),
      pctOfServePlus1Shots: pct(arr.length, sp1Pts.length || arr.length || 1) || 0,
      serverWonPct: pct(won, arr.length),
      insideCourtPct: pct(insideCourt, positioned.length),
      behindBaselinePct: pct(behindBaseline, positioned.length),
    };
  };
  const servePlus1ByHand = {
    fh: servePlus1HandStat(0), // kForehand
    bh: servePlus1HandStat(1), // kBackhand
  };

  // Return by hand (focused player's return when returning, or opponent's
  // when serving — the "returnHand" field is always the receiver's hand).
  const returnHandStat = (handVal) => {
    const arr = points.filter((p) => p.returnHand === handVal);
    const receiverWon = arr.filter(
      (p) => p.pointWonByServer === false && p.serveShot?.cls !== "out"
    ).length;
    const positioned = arr.filter((p) => p.returnInsideBaseline !== null);
    const insideCourt = positioned.filter((p) => p.returnInsideBaseline === true).length;
    const behindBaseline = positioned.filter((p) => p.returnInsideBaseline === false).length;
    return {
      attempts: arr.length,
      pctOfAllReturns: pct(arr.length, total),
      receiverWonPct: pct(receiverWon, arr.length),
      insideCourtPct: pct(insideCourt, positioned.length),
      behindBaselinePct: pct(behindBaseline, positioned.length),
    };
  };
  const returnByHand = {
    fh: returnHandStat(0),
    bh: returnHandStat(1),
  };

  // Return+1 by hand (returner's next shot after the return).
  const returnPlus1HandStat = (handVal) => {
    const arr = points.filter((p) => p.returnPlus1Hand === handVal);
    const receiverWon = arr.filter((p) => p.pointWonByServer === false).length;
    const positioned = arr.filter((p) => p.returnPlus1InsideBaseline !== null);
    const insideCourt = positioned.filter((p) => p.returnPlus1InsideBaseline === true).length;
    const behindBaseline = positioned.filter((p) => p.returnPlus1InsideBaseline === false).length;
    return {
      opportunities: arr.length,
      pctOfAllPoints: pct(arr.length, total),
      receiverWonPct: pct(receiverWon, arr.length),
      insideCourtPct: pct(insideCourt, positioned.length),
      behindBaselinePct: pct(behindBaseline, positioned.length),
    };
  };
  const returnPlus1ByHand = {
    fh: returnPlus1HandStat(0),
    bh: returnPlus1HandStat(1),
  };

  // ── Serve / return effectiveness on breakpoints ─────────────────────────
  const bpPoints = points.filter((p) => p.isBreakpoint);
  const servesIn = points.filter((p) => p.serveIn).length;
  const bpServesIn = bpPoints.filter((p) => p.serveIn).length;
  const returnsIn = points.filter((p) => p.returnIn).length;
  const bpReturnsIn = bpPoints.filter((p) => p.returnIn).length;

  // Note: isBreakpoint is computed from the serve event (server's perspective).
  // It is true when the receiver has break point (== server is facing BP).
  // Therefore, in the SERVING section breakpointPoints = points where the
  // focused player FACED break point. In the RETURNING section
  // breakpointPoints = points where the focused player HAD break point.
  const breakpoint = {
    breakpointPoints: bpPoints.length,
    serveInPctOnBreakpoint: pct(bpServesIn, bpPoints.length),
    returnInPctOnBreakpoint: pct(bpReturnsIn, bpPoints.length),
  };

  const overallServerWon = points.filter((p) => p.pointWonByServer).length;

  return {
    totalPoints: total,
    serverWonPct: pct(overallServerWon, total),
    serveInPct: pct(servesIn, total),
    returnInPct: pct(returnsIn, total),
    serveZones: serveZoneStats,
    returnZones: returnZoneStats,
    servePlus1Zones: servePlus1Stats,
    servePlus1ByHand,
    returnByHand,
    returnPlus1ByHand,
    breakpoint,
  };
}

function buildAISummary({ playerName, firstServing, secondServing, firstReturning, secondReturning, matches, playerGuid, teamRecord = null }) {
  const summarise = (sides) => ({
    deuce: summariseSidePoints(sides.deuce),
    ad: summariseSidePoints(sides.ad),
    all: summariseSidePoints(sides.all),
  });
  const doublesPlayerProfiles = (matches && playerGuid)
    ? buildDoublesPlayerStats(matches, playerGuid)
    : null;
  // Enrich per-player profiles with accumulated court-side data from the stored team record.
  if (doublesPlayerProfiles && teamRecord) {
    const memberKeys = [
      { playerKey: "player1", sideKey: "player1CourtSide", statsKey: "player1CourtSideStats" },
      { playerKey: "player2", sideKey: "player2CourtSide", statsKey: "player2CourtSideStats" },
    ];
    for (const prof of doublesPlayerProfiles) {
      const mk = memberKeys.find((m) => {
        const p = teamRecord[m.playerKey];
        if (!p) return false;
        const fn = (p.firstName ?? "").trim().toLowerCase();
        const ln = (p.lastName ?? "").trim().toLowerCase();
        return prof.name.toLowerCase().includes(fn) || prof.name.toLowerCase().includes(ln);
      }) ?? memberKeys.find((m) => teamRecord[m.playerKey]);
      if (mk && teamRecord[mk.sideKey]) {
        prof.accumulatedCourtSide = teamRecord[mk.sideKey];
        if (teamRecord[mk.statsKey]) prof.accumulatedCourtSideStats = teamRecord[mk.statsKey];
      }
    }
  }
  return {
    focusedPlayer: playerName,
    ...(teamRecord?.preferredFormation && { preferredFormation: teamRecord.preferredFormation }),
    serving: {
      firstServe: summarise(firstServing),
      secondServe: summarise(secondServing),
    },
    returning: {
      firstServe: summarise(firstReturning),
      secondServe: summarise(secondReturning),
    },
    doublesPlayerProfiles,
    legend: {
      serveZones: "Wide / Body / T (from server's perspective in each service box)",
      returnZones: "Z1..Z6 across the singles court (Z1 = far Ad-court sideline, Z6 = far Deuce-court sideline)",
      hands: "fh = forehand, bh = backhand",
      breakpoint: "breakpointPoints counts points where one side held break point. In the SERVING section the focused player was FACING break point (so serveInPctOnBreakpoint = focused player's 1st/2nd serve in % when facing BP). In the RETURNING section the focused player HAD break point (so returnInPctOnBreakpoint = focused player's return-in-play % when HAVING break point).",
      servePlus1ByHand: "Server's first shot after the return. opportunities = # times server hit a serve+1 with that hand. serverWonPct = point win % when serve+1 was that hand. insideCourtPct = % of serve+1 shots played from inside the court (contact point past the baseline toward the net — attacking position). behindBaselinePct = % of serve+1 shots played from behind the baseline (defensive position). The two %s sum to ~100.",
      returnByHand: "Receiver's return. attempts = # returns played with that hand. receiverWonPct = % of those points won by receiver. insideCourtPct = % of returns played from inside the court (contact past the receiver's baseline toward the net — attacking position). behindBaselinePct = % played from behind the baseline (defensive). The two %s sum to ~100.",
      returnPlus1ByHand: "Receiver's first shot after their return. receiverWonPct = % of those points won by receiver. insideCourtPct / behindBaselinePct = court position of the return+1 (same convention as returnByHand).",
      metrics: "serverWonPct = % of points server won; returnerWonPct = % of points receiver won; aces = clean serve winners; faults = serves long/wide/net; serveInPct = % of attempted serves that went in.",
    },
  };
}

const AI_SYSTEM_PROMPT = `You are an elite tennis performance analyst. You are given aggregated serve, return and shot-placement statistics for a focused player/team both when serving and when returning.

Produce a tactical report in markdown with these sections. Reference specific numbers from the data, and flag any sample size below 10.

## Serving Profile (when ${"{PLAYER}"} is serving)
- Serve placement tendencies on Deuce vs Ad, 1st vs 2nd; strongest and weakest zones by server-won %.
- **Serve+1 effectiveness by hand**: forehand vs backhand serve+1 — which side is more effective and what % of points are won when serve+1 is forehand vs backhand.
- **Serve+1 forehand opportunities** created (count and % of all service points).
- **Serve+1 forehand court position**: report BOTH the % played from inside the court AND the % played from behind the baseline (these sum to ~100 and indicate attacking vs defensive +1 posture).
- **First-serve % when facing breakpoint** (clutch serving — use the SERVING section's breakpoint.serveInPctOnBreakpoint, where breakpointPoints = times ${"{PLAYER}"} faced BP).

## Returning Profile (when ${"{PLAYER}"} is returning)
- Forehand vs backhand return: win/loss % for each, plus court position (% inside the court vs % behind the baseline) for each side.
- Return+1 effectiveness by hand — which side wins more points after the return, and the court position (inside court vs behind baseline) of those return+1 shots.
- **% of returns put back into play overall**, and **specifically when ${"{PLAYER}"} HAS break point** (use the RETURNING section's breakpoint.returnInPctOnBreakpoint — in this section breakpointPoints = times ${"{PLAYER}"} had BP on the opponent's serve).

## How to Exploit ${"{PLAYER}"}
### When they are SERVING (you are returning)
- Concrete return targets and which serve types/zones to attack based on the serve+1 weaknesses identified.
- Pressure points where their first-serve % drops on BP.

### When they are RETURNING (you are serving)
- Best serve placements to target (Deuce/Ad, Wide/Body/T, 1st/2nd).
- Recommended serve+1 patterns based on which hand they are weaker returning with and whether they tend to return from inside the court or behind the baseline.

Be specific, reference numbers, avoid fluff.`;

// Extra prompt injected when doublesPlayerProfiles data is present.
const AI_DOUBLES_PLAYER_PROMPT = `
## Doubles: Individual Player Profiles

The data also includes doublesPlayerProfiles — an array of per-player stats for each member of the focused team. For each player, write a sub-section:

### [Player Name] — Court Role & Shot Profile
- **Court side preference**: use rightCourtPct / leftCourtPct from real-time data and, if present, accumulatedCourtSide (cross-match history). rightCourtPct >= 55 → deuce-court specialist; leftCourtPct >= 55 → ad-court specialist. State the tactical implications (deuce player serves from deuce side, tends to attack down the centre on ad; ad player covers the middle on deuce and exploits inside-out forehand/backhand).
- **Net presence**: atNetPct (% of ALL their coded events played at net), netShotPct (% of rally shots that are volleys/overheads/etc.), poachCount (poach intercepts).
- **Shot type breakdown**: list topShotTypes with percentages. Identify whether they are primarily a volleyer/net player or a baseline rallier.
- **Hand profile**: forehandPct / backhandPct. How does this interact with their court-side position?
- **Effectiveness**: winnerPct and errorPct from rally shots.

## How to Exploit Each Player Individually
### Targeting [Player 1 Name] (deuce/ad — state which)
- Specific serve directions, return targets and rally patterns to expose them given their court side, shot tendencies and error rate.
- Whether their poach habit creates exploitable gaps (lobbing, going behind them).
- Net vs baseline positioning implications: if high atNetPct → lob them or drive low at their feet; if rarely at net → drag them to the net with short balls.

### Targeting [Player 2 Name] (deuce/ad — state which)
- Same structure as above.

## Doubles Team Coordination Patterns (${"{PLAYER}"})
- How the two players' court sides and net tendencies complement or conflict with each other.
- **Formation**: if preferredFormation is in the data, explain what it means tactically — which player serves from which side, net player starting position, and the specific advantages/vulnerabilities. "Australian Formation" → net player starts on the same side as server and crosses after serve (receiver must hit cross-court return; exploit with lob or sharp angle). "I Formation" → net player centralised, uses late hand signals to choose which way to cross (creates uncertainty; exploit with body serves or deep returns before they can poach). "2-Back Formation" → both players baseline (forfeit net pressure; exploit with short angles to pull them wide). "Standard Formation" → conventional diagonal, note poach tendencies.
- Whether one player consistently finishes points from the net (high netShotPct + winnerPct) while the other plays from the baseline.
- Recommended tactical pattern to break their coordination (e.g., constant lobs to keep net player back, serving wide to open angles, targeting the weaker returner, driving at the net player's feet to prevent poaching).
`;

async function streamOpenAIChat({ apiKey, model, messages, signal, onChunk }) {
  const res = await fetch("https://api.openai.com/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({ model, stream: true, messages }),
    signal,
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(errBody?.error?.message ?? `OpenAI API error ${res.status}`);
  }

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6).trim();
      if (data === "[DONE]") return;
      try {
        const json = JSON.parse(data);
        const delta = json?.choices?.[0]?.delta?.content;
        if (delta) onChunk(delta);
      } catch {
        /* ignore parse errors on keep-alives */
      }
    }
  }
}

function buildBaseMessages({ summary, playerName }) {
  const systemPrompt = AI_SYSTEM_PROMPT.replaceAll("{PLAYER}", playerName);
  const messages = [
    { role: "system", content: systemPrompt },
  ];
  // Inject doubles per-player section when profiles are available.
  if (summary?.doublesPlayerProfiles?.length > 0) {
    messages.push({
      role: "system",
      content: AI_DOUBLES_PLAYER_PROMPT.replaceAll("{PLAYER}", playerName),
    });
  }
  messages.push({
    role: "system",
    content:
      `Reference data for ${playerName} (JSON):\n\n` +
      JSON.stringify(summary, null, 2) +
      `\n\nUse this data for any analysis or follow-up questions.`,
  });
  return messages;
}

function AIReportPanel({ matches, playerGuid, playerName, specificPlayerGuid = null, teamRecord = null }) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(OPENAI_KEY_LS) ?? "");
  const [model, setModel] = useState(() => localStorage.getItem(OPENAI_MODEL_LS) ?? "gpt-4o-mini");
  const [keyDraft, setKeyDraft] = useState("");
  const [modelDraft, setModelDraft] = useState("");
  // Follow-up Q&A: list of {role:'user'|'assistant', content:string}
  const [followUps, setFollowUps] = useState([]);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  // Saved questions persisted across sessions, most-recent first.
  const [savedQuestions, setSavedQuestions] = useState(() => loadSavedQuestions());
  const abortRef = useRef(null);

  const summary = useMemo(() => {
    if (matches.length === 0) return null;
    return buildAISummary({
      playerName,
      firstServing: buildServePoints(matches, playerGuid, kServeFirst, "serving", specificPlayerGuid),
      secondServing: buildServePoints(matches, playerGuid, kServeSecond, "serving", specificPlayerGuid),
      firstReturning: buildServePoints(matches, playerGuid, kServeFirst, "returning", specificPlayerGuid),
      secondReturning: buildServePoints(matches, playerGuid, kServeSecond, "returning", specificPlayerGuid),
      matches,
      playerGuid,
      teamRecord,
    });
  }, [matches, playerGuid, playerName, specificPlayerGuid, teamRecord]);

  const openSettings = () => {
    setKeyDraft(apiKey);
    setModelDraft(model);
    setShowSettings(true);
  };
  const saveSettings = () => {
    const trimmed = keyDraft.trim();
    setApiKey(trimmed);
    setModel(modelDraft);
    localStorage.setItem(OPENAI_KEY_LS, trimmed);
    localStorage.setItem(OPENAI_MODEL_LS, modelDraft);
    setShowSettings(false);
  };

  const generate = async () => {
    if (!apiKey) {
      openSettings();
      return;
    }
    if (!summary) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setReport("");
    setFollowUps([]);
    setError("");
    setGenerating(true);
    try {
      const baseMessages = buildBaseMessages({ summary, playerName });
      await streamOpenAIChat({
        apiKey,
        model,
        messages: [
          ...baseMessages,
          { role: "user", content: "Write the tactical report now." },
        ],
        signal: ctrl.signal,
        onChunk: (delta) => setReport((prev) => prev + delta),
      });
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message ?? String(e));
    } finally {
      setGenerating(false);
    }
  };

  const askFollowUp = async () => {
    const q = question.trim();
    if (!q) return;
    if (!apiKey) {
      openSettings();
      return;
    }
    if (!summary || !report) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setError("");
    setQuestion("");
    // Persist this question (de-duped, most-recent first).
    setSavedQuestions((prev) => {
      const next = [q, ...prev.filter((s) => s !== q)].slice(0, MAX_SAVED_QUESTIONS);
      persistSavedQuestions(next);
      return next;
    });
    // Append user question + a placeholder assistant entry that we'll stream into.
    const newFollowUps = [
      ...followUps,
      { role: "user", content: q },
      { role: "assistant", content: "" },
    ];
    setFollowUps(newFollowUps);
    const assistantIdx = newFollowUps.length - 1;
    setAsking(true);
    try {
      const baseMessages = buildBaseMessages({ summary, playerName });
      // Replay full conversation: initial report + prior follow-ups + this question.
      const history = [
        ...baseMessages,
        { role: "user", content: "Write the tactical report now." },
        { role: "assistant", content: report },
        ...followUps.map((m) => ({ role: m.role, content: m.content })),
        { role: "user", content: q },
      ];
      await streamOpenAIChat({
        apiKey,
        model,
        messages: history,
        signal: ctrl.signal,
        onChunk: (delta) =>
          setFollowUps((prev) => {
            const next = prev.slice();
            if (next[assistantIdx]) {
              next[assistantIdx] = {
                ...next[assistantIdx],
                content: next[assistantIdx].content + delta,
              };
            }
            return next;
          }),
      });
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message ?? String(e));
    } finally {
      setAsking(false);
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setGenerating(false);
    setAsking(false);
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(report);
    } catch {
      /* ignore */
    }
  };

  const [savingPdf, setSavingPdf] = useState(false);

  const savePdf = async () => {
    if (!report) return;
    setSavingPdf(true);
    try {
      const { default: jsPDF } = await import("jspdf");

      const MARGIN = 15;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const maxX = pageW - MARGIN;
      const contentW = pageW - MARGIN * 2;
      let y = MARGIN;

      const checkPage = (needed = 6) => {
        if (y + needed > pageH - MARGIN) {
          pdf.addPage();
          y = MARGIN;
        }
      };

      const writeLine = (text, opts = {}) => {
        const { size = 10, bold = false, color = [30, 30, 30], indent = 0 } = opts;
        pdf.setFontSize(size);
        pdf.setFont("helvetica", bold ? "bold" : "normal");
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(text, contentW - indent);
        lines.forEach((ln) => {
          checkPage(size * 0.35 + 1);
          pdf.text(ln, MARGIN + indent, y);
          y += size * 0.35 + 1;
        });
      };

      // Cover banner
      pdf.setFillColor(30, 100, 200);
      pdf.rect(0, 0, pageW, 28, "F");
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.text("Shots Analysis – AI Coaching Report", MARGIN, 12);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(playerName, MARGIN, 19);
      const matchCount = matches.length;
      pdf.text(
        `${matchCount} match${matchCount !== 1 ? "es" : ""}  ·  ${new Date().toLocaleDateString()}`,
        MARGIN,
        24
      );
      pdf.setTextColor(30, 30, 30);
      y = 36;

      // Body (markdown parse) — reusable helper.
      const renderMarkdown = (md) => {
        const lines = md.split("\n");
        for (const line of lines) {
          if (line.startsWith("## ")) {
            y += 3;
            checkPage(12);
            pdf.setFontSize(12);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(30, 100, 200);
            pdf.text(line.slice(3), MARGIN, y);
            y += 1.5;
            pdf.setDrawColor(30, 100, 200);
            pdf.setLineWidth(0.3);
            pdf.line(MARGIN, y, maxX, y);
            y += 4;
            pdf.setTextColor(30, 30, 30);
          } else if (line.startsWith("### ")) {
            y += 2;
            writeLine(line.slice(4), { size: 10.5, bold: true });
            y += 1;
          } else if (line.startsWith("- ") || line.startsWith("* ")) {
            const text = line.slice(2).replace(/\*\*([^*]+)\*\*/g, "$1");
            checkPage(6);
            pdf.setFontSize(10);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(30, 30, 30);
            pdf.text("•", MARGIN + 1, y);
            const wrapped = pdf.splitTextToSize(text, contentW - 7);
            for (let wi = 0; wi < wrapped.length; wi++) {
              if (wi > 0) checkPage(5);
              pdf.text(wrapped[wi], MARGIN + 6, y);
              y += 4.5;
            }
          } else if (!line.trim()) {
            y += 2;
          } else {
            const text = line.replace(/\*\*([^*]+)\*\*/g, "$1");
            writeLine(text, { size: 10 });
            y += 0.5;
          }
        }
      };

      renderMarkdown(report);

      // Follow-up Q&A section.
      if (followUps.length > 0) {
        y += 4;
        checkPage(16);
        pdf.setFontSize(13);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(30, 100, 200);
        pdf.text("Follow-up Questions", MARGIN, y);
        y += 2;
        pdf.setDrawColor(30, 100, 200);
        pdf.setLineWidth(0.3);
        pdf.line(MARGIN, y, maxX, y);
        y += 5;
        pdf.setTextColor(30, 30, 30);

        for (const m of followUps) {
          if (!m.content?.trim()) continue;
          if (m.role === "user") {
            y += 2;
            checkPage(10);
            // Question label
            pdf.setFontSize(10);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(30, 100, 200);
            pdf.text("Q:", MARGIN, y);
            pdf.setTextColor(30, 30, 30);
            const wrapped = pdf.splitTextToSize(m.content, contentW - 7);
            for (let wi = 0; wi < wrapped.length; wi++) {
              if (wi > 0) checkPage(5);
              pdf.text(wrapped[wi], MARGIN + 6, y);
              y += 4.5;
            }
            y += 1;
          } else {
            checkPage(8);
            pdf.setFontSize(10);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(30, 100, 200);
            pdf.text("A:", MARGIN, y);
            pdf.setTextColor(30, 30, 30);
            // Advance past the "A:" label so the markdown body doesn't overwrite it.
            y += 5;
            renderMarkdown(m.content);
            y += 2;
          }
        }
      }

      // Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7.5);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(160, 160, 160);
        pdf.text(
          `TennisStats Pro  ·  ${new Date().toLocaleDateString()}  ·  Page ${i} of ${totalPages}`,
          pageW / 2,
          pageH - 6,
          { align: "center" }
        );
      }

      const safeName = (playerName || "Player").replace(/[^a-z0-9]/gi, "_");
      pdf.save(`AI_ShotsAnalysis_${safeName}.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
    } finally {
      setSavingPdf(false);
    }
  };

  const reportBoxRef = useRef(null);
  const didScrollPanelIntoView = useRef(false);
  useEffect(() => {
    const streaming = generating || asking;
    if (!streaming) {
      didScrollPanelIntoView.current = false;
      return;
    }
    if (!didScrollPanelIntoView.current && reportBoxRef.current) {
      reportBoxRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      didScrollPanelIntoView.current = true;
    }
    const box = reportBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [report, generating, asking, followUps]);

  return (
    <>
      <div className="card bg-base-200 shadow-sm">
        <div className="card-body p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="card-title text-base">AI Coaching Report</h2>
            <div className="flex items-center gap-2">
              {report && !generating && (
                <button className="btn btn-ghost btn-xs" onClick={copyReport}>
                  Copy
                </button>
              )}
              {report && !generating && (
                <button className="btn btn-ghost btn-xs" onClick={savePdf} disabled={savingPdf}>
                  {savingPdf ? "Saving…" : "Download PDF"}
                </button>
              )}
              <button className="btn btn-ghost btn-xs" onClick={openSettings}>
                Settings
              </button>
              {!open ? (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setOpen(true);
                    if (!report) generate();
                  }}
                  disabled={!summary}
                >
                  Generate Report
                </button>
              ) : (
                <>
                  {generating ? (
                    <button className="btn btn-warning btn-sm" onClick={stop}>
                      Stop
                    </button>
                  ) : (
                    <button className="btn btn-primary btn-sm" onClick={generate} disabled={!summary}>
                      {report ? "Regenerate" : "Generate"}
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
                    Hide
                  </button>
                </>
              )}
            </div>
          </div>

          {open && (
            <div className="mt-3">
              {error && <div className="alert alert-error text-sm mb-2">{error}</div>}
              {generating && !report && (
                <div className="flex items-center gap-2 text-sm opacity-70">
                  <span className="loading loading-spinner loading-sm" />
                  Analysing placement data…
                </div>
              )}
              {(report || generating) && (
                <div
                  ref={reportBoxRef}
                  className="bg-base-100 rounded-box border border-base-300 px-6 py-5 max-h-[60vh] overflow-y-auto"
                >
                  <MarkdownReport text={report} />
                  {generating && (
                    <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5 rounded-sm align-middle" />
                  )}

                  {followUps.length > 0 && (
                    <div className="mt-6 space-y-4">
                      {followUps.map((m, i) => (
                        <div key={i}>
                          {m.role === "user" ? (
                            <div className="chat chat-end">
                              <div className="chat-bubble chat-bubble-primary whitespace-pre-wrap">
                                {m.content}
                              </div>
                            </div>
                          ) : (
                            <div className="border-t border-base-300 pt-4">
                              <MarkdownReport text={m.content} />
                              {asking && i === followUps.length - 1 && (
                                <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5 rounded-sm align-middle" />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {report && !generating && (
                <form
                  className="mt-3 space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!asking) askFollowUp();
                  }}
                >
                  {savedQuestions.length > 0 && (
                    <div className="flex items-center gap-2">
                      <select
                        className="select select-bordered select-sm flex-1"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) setQuestion(e.target.value);
                        }}
                        disabled={asking}
                      >
                        <option value="">Saved questions…</option>
                        {savedQuestions.map((q, i) => (
                          <option key={i} value={q}>
                            {q.length > 90 ? q.slice(0, 87) + "…" : q}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        onClick={() => {
                          setSavedQuestions([]);
                          persistSavedQuestions([]);
                        }}
                        disabled={asking}
                        title="Clear saved questions"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      className="textarea textarea-bordered flex-1 min-h-[3rem] max-h-40"
                      placeholder={`Ask a follow-up about ${playerName}… (Enter to send, Shift+Enter for newline)`}
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (!asking) askFollowUp();
                        }
                      }}
                      disabled={asking}
                      rows={2}
                    />
                    {asking ? (
                      <button type="button" className="btn btn-warning" onClick={stop}>
                        Stop
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!question.trim()}
                      >
                        Ask
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {showSettings && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-3">OpenAI Settings</h3>
            <label className="label">
              <span className="label-text">API Key</span>
            </label>
            <input
              type="password"
              className="input input-bordered w-full"
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value)}
              placeholder="sk-..."
            />
            <label className="label mt-3">
              <span className="label-text">Model</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              value={modelDraft}
              onChange={(e) => setModelDraft(e.target.value)}
              placeholder="gpt-4o-mini"
            />
            <div className="modal-action">
              <button className="btn btn-ghost" onClick={() => setShowSettings(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveSettings}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ShotsProfileReport() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { firebaseUser } = useAuthStatus();
  const uid = firebaseUser?.uid ?? null;

  const matchGuids = useMemo(() => state?.matchGuids ?? [], [state]);
  const playerGuid = state?.playerGuid ?? null;

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamRecord, setTeamRecord] = useState(null);
  const [sideFilter, setSideFilter] = useState("deuce");
  const [hideErrors, setHideErrors] = useState(false);
  const [showPaths, setShowPaths] = useState(false);
  const [serveType, setServeType] = useState("first"); // "first" | "second"
  const [perspective, setPerspective] = useState("serving"); // "serving" | "returning"
  const [selectedDoublesPlayer, setSelectedDoublesPlayer] = useState(null); // null = whole team

  const focusedPlayerName = useMemo(() => {
    for (const m of matches) {
      if (m.isDoubles) {
        const tAGuid = m.teamA?.guid ||
          (m.teamA?.player1?.guid && m.teamA?.player2?.guid
            ? `${m.teamA.player1.guid}_${m.teamA.player2.guid}` : null);
        const tBGuid = m.teamB?.guid ||
          (m.teamB?.player1?.guid && m.teamB?.player2?.guid
            ? `${m.teamB.player1.guid}_${m.teamB.player2.guid}` : null);
        let team = null;
        if (playerGuid === tAGuid || playerGuid === m.teamA?.player1?.guid || playerGuid === m.teamA?.player2?.guid) {
          team = m.teamA;
        } else if (playerGuid === tBGuid || playerGuid === m.teamB?.player1?.guid || playerGuid === m.teamB?.player2?.guid) {
          team = m.teamB;
        }
        if (team) {
          if (team.name?.trim()) return team.name.trim();
          const p1 = `${team.player1?.firstName ?? ""} ${team.player1?.lastName ?? ""}`.trim();
          const p2 = `${team.player2?.firstName ?? ""} ${team.player2?.lastName ?? ""}`.trim();
          const combined = [p1, p2].filter(Boolean).join(" / ");
          if (combined) return combined;
        }
        continue;
      }
      const p1Guid = m?.player1?.guid ?? m?.player1Guid;
      const p2Guid = m?.player2?.guid ?? m?.player2Guid;
      let p = null;
      if (p1Guid === playerGuid || m?.player1Guid === playerGuid) p = m?.player1;
      else if (p2Guid === playerGuid || m?.player2Guid === playerGuid) p = m?.player2;
      if (p) {
        const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim();
        if (name) return name;
      }
    }
    return "Focused Player";
  }, [matches, playerGuid]);

  // Extract the two individual players of the focused doubles team (null for singles).
  const doublesTeamMembers = useMemo(() => {
    for (const m of matches) {
      if (!m.isDoubles) continue;
      const tAGuid = m.teamA?.guid ||
        (m.teamA?.player1?.guid && m.teamA?.player2?.guid
          ? `${m.teamA.player1.guid}_${m.teamA.player2.guid}` : null);
      const tBGuid = m.teamB?.guid ||
        (m.teamB?.player1?.guid && m.teamB?.player2?.guid
          ? `${m.teamB.player1.guid}_${m.teamB.player2.guid}` : null);
      let team = null;
      if (
        playerGuid === tAGuid ||
        playerGuid === m.teamA?.player1?.guid ||
        playerGuid === m.teamA?.player2?.guid
      ) team = m.teamA;
      else if (
        playerGuid === tBGuid ||
        playerGuid === m.teamB?.player1?.guid ||
        playerGuid === m.teamB?.player2?.guid
      ) team = m.teamB;
      if (team?.player1?.guid && team?.player2?.guid) {
        return [
          {
            guid: team.player1.guid,
            name: `${team.player1.firstName ?? ""} ${team.player1.lastName ?? ""}`.trim() || team.player1.guid.slice(0, 8),
          },
          {
            guid: team.player2.guid,
            name: `${team.player2.firstName ?? ""} ${team.player2.lastName ?? ""}`.trim() || team.player2.guid.slice(0, 8),
          },
        ];
      }
      break;
    }
    return null;
  }, [matches, playerGuid]);

  const selectedMember = doublesTeamMembers?.find((p) => p.guid === selectedDoublesPlayer) ?? null;
  const effectivePlayerName = selectedMember?.name ?? focusedPlayerName;

  useEffect(() => {
    if (!uid || matchGuids.length === 0) return;
    setLoading(true);
    Promise.all(matchGuids.map((g) => fetchMatchWithData(uid, g)))
      .then((res) => setMatches(res.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [uid, matchGuids]);

  // Fetch the persisted team record (preferredFormation, accumulated courtSide) for doubles teams.
  useEffect(() => {
    if (!uid || !playerGuid) return;
    fetchTeamByGuid(uid, playerGuid)
      .then((rec) => setTeamRecord(rec ?? null))
      .catch(() => setTeamRecord(null));
  }, [uid, playerGuid]);

  const firstServePoints = useMemo(
    () => buildServePoints(matches, playerGuid, kServeFirst, perspective, selectedDoublesPlayer),
    [matches, playerGuid, perspective, selectedDoublesPlayer]
  );

  const secondServePoints = useMemo(
    () => buildServePoints(matches, playerGuid, kServeSecond, perspective, selectedDoublesPlayer),
    [matches, playerGuid, perspective, selectedDoublesPlayer]
  );

  if (!playerGuid) {
    return (
      <div className="p-4">
        <button className="btn btn-ghost btn-sm mb-3" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="alert alert-warning">No player selected for Shots Analysis report.</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3rem)] overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-2 border-b border-base-300 shrink-0">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 className="text-lg font-bold">Shots Analysis</h1>
        {effectivePlayerName !== "Focused Player" && (
          <span className="text-base text-base-content/60">— {effectivePlayerName}</span>
        )}
        {matchGuids.length > 1 && (
          <span className="badge badge-primary">{matchGuids.length} matches combined</span>
        )}
        {loading && <span className="loading loading-spinner loading-sm" />}
      </div>

      <div className="px-4 py-3 border-b border-base-300 flex items-center gap-2 shrink-0 flex-wrap">
        <span className="text-sm font-medium">Server:</span>
        <button
          className={`btn btn-xs ${perspective === "serving" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setPerspective("serving")}
        >
          {effectivePlayerName}
        </button>
        <button
          className={`btn btn-xs ${perspective === "returning" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setPerspective("returning")}
        >
          OPPOSITIONS
        </button>
        {doublesTeamMembers && (
          <>
            <div className="divider divider-horizontal mx-1" />
            <span className="text-sm font-medium">Player:</span>
            <button
              className={`btn btn-xs ${!selectedDoublesPlayer ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setSelectedDoublesPlayer(null)}
            >
              Both
            </button>
            {doublesTeamMembers.map((p) => (
              <button
                key={p.guid}
                className={`btn btn-xs ${selectedDoublesPlayer === p.guid ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setSelectedDoublesPlayer(p.guid)}
              >
                {p.name}
              </button>
            ))}
          </>
        )}
        <div className="divider divider-horizontal mx-1" />
        <span className="text-sm font-medium">Serve:</span>
        <button
          className={`btn btn-xs ${serveType === "first" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setServeType("first")}
        >
          First
        </button>
        <button
          className={`btn btn-xs ${serveType === "second" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setServeType("second")}
        >
          Second
        </button>
        <div className="divider divider-horizontal mx-1" />
        <span className="text-sm font-medium">Side:</span>
        <button
          className={`btn btn-xs ${sideFilter === "all" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setSideFilter("all")}
        >
          Deuce + Ad
        </button>
        <button
          className={`btn btn-xs ${sideFilter === "deuce" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setSideFilter("deuce")}
        >
          Deuce
        </button>
        <button
          className={`btn btn-xs ${sideFilter === "ad" ? "btn-primary" : "btn-ghost"}`}
          onClick={() => setSideFilter("ad")}
        >
          Ad
        </button>
        <div className="divider divider-horizontal mx-1" />
        <label className="label cursor-pointer gap-2 py-0">
          <span className="label-text text-sm">Hide errors</span>
          <input
            type="checkbox"
            className="toggle toggle-sm toggle-primary"
            checked={hideErrors}
            onChange={(e) => setHideErrors(e.target.checked)}
          />
        </label>
        <label className="label cursor-pointer gap-2 py-0">
          <span className="label-text text-sm">Show ball paths</span>
          <input
            type="checkbox"
            className="toggle toggle-sm toggle-primary"
            checked={showPaths}
            onChange={(e) => setShowPaths(e.target.checked)}
          />
        </label>
      </div>

      <div className="overflow-y-auto flex-1 p-4 flex flex-col gap-5">
        <AIReportPanel matches={matches} playerGuid={playerGuid} playerName={effectivePlayerName} specificPlayerGuid={selectedDoublesPlayer} teamRecord={teamRecord} />
        {serveType === "first" ? (
          <ServeSection
            title={perspective === "serving" ? "First Serve" : "First Serve (Returning)"}
            points={firstServePoints}
            sideFilter={sideFilter}
            hideErrors={hideErrors}
            showPaths={showPaths}
          />
        ) : (
          <ServeSection
            title={perspective === "serving" ? "Second Serve" : "Second Serve (Returning)"}
            points={secondServePoints}
            sideFilter={sideFilter}
            hideErrors={hideErrors}
            showPaths={showPaths}
          />
        )}
      </div>
    </div>
  );
}

export default ShotsProfileReport;
